import {ssrSafeDocument} from '@github-ui/ssr-utils'

/**
 * Tracks pointer exits for any number of elements with a single document-level pointer listener.
 * Each observer gets notified either when the pointer moves outside of its element.
 */
type PointerExitObserver = {
  element: HTMLElement
  shouldIgnore?: () => boolean
  onPointerExit: () => void
}

type TrackedPointerExitObserver = PointerExitObserver & {pendingExit: boolean}

const observers = new Set<TrackedPointerExitObserver>()
let isListening = false

const handlePointerMove = (event: PointerEvent) => {
  const target = event.target instanceof Node ? event.target : null

  for (const observer of observers) {
    const {element, shouldIgnore, onPointerExit} = observer
    if (!element.isConnected) {
      observers.delete(observer)
      continue
    }

    if (target && element.contains(target)) {
      observer.pendingExit = false
      continue
    }

    observer.pendingExit = true

    if (shouldIgnore?.()) {
      continue
    }

    observer.pendingExit = false
    onPointerExit()
  }

  if (observers.size === 0) {
    teardownListener()
  }
}

const ensureListener = () => {
  if (isListening || !ssrSafeDocument) return
  ssrSafeDocument.addEventListener('pointermove', handlePointerMove)
  isListening = true
}

const teardownListener = () => {
  if (!isListening || !ssrSafeDocument || observers.size > 0) return
  ssrSafeDocument.removeEventListener('pointermove', handlePointerMove)
  isListening = false
}

export function registerPointerExitObserver(observer: PointerExitObserver) {
  const {element} = observer
  if (!element) return () => {}

  const trackedObserver: TrackedPointerExitObserver = {...observer, pendingExit: false}
  observers.add(trackedObserver)
  ensureListener()

  return () => {
    observers.delete(trackedObserver)
    teardownListener()
  }
}
